import { useTranslation } from 'react-i18next';
import { useWatch, type UseFormReturn } from 'react-hook-form';

import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/shared/ui';

import { CONFIRM_ARRIVAL_TEXT_MAX_LENGTH } from '../model/constants';
import type { ConfirmArrivalFormValues } from '../model/types';

interface ConfirmArrivalLossExplanationProps {
  form: UseFormReturn<ConfirmArrivalFormValues>;
}

/**
 * Appears with the first loss entered, and is mandatory from then on — the
 * backend rejects a body that reports a loss without it.
 *
 * Watches the quantities from its own component so a keystroke in the lines
 * table does not re-render the whole header.
 */
export function ConfirmArrivalLossExplanation({ form }: ConfirmArrivalLossExplanationProps) {
  const { t } = useTranslation();
  const lines = useWatch({ control: form.control, name: 'lines' });
  const hasLoss = (lines ?? []).some((line) => line.lostQty > 0);

  if (!hasLoss) return null;

  return (
    <FormField
      label={t('receive.loss.comments.label', 'Explanation of Losses')}
      name="explanationOfLosses"
      error={form.formState.errors.explanationOfLosses?.message}
      required>
      <Textarea
        {...form.register('explanationOfLosses')}
        id="explanationOfLosses"
        rows={2}
        maxLength={CONFIRM_ARRIVAL_TEXT_MAX_LENGTH}
        placeholder={t('receive.loss.comments.label', 'Explanation of Losses')}
      />
    </FormField>
  );
}
